import cn from 'clsx'
import React, { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Track } from 'types/track'
import type { EventSource, TrackLocation } from 'types/ui'
import type { TrackOptions } from '@disco/state-management/actions/MediaActions'
import {
  pauseTrack,
  playTrack,
} from '@disco/state-management/actions/MediaActions'
import GoogleAnalytics from '@disco/api/ga'
import mixpanel from '@disco/api/mixpanel'
import type { FileType } from '@disco/constants/FileTypes'
import { AUDIO_FILE, DRM_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { LOADING, PLAYING } from '@disco/constants/MediaStatus'
import type { TrackStatus } from '@disco/state-management/reducers/media'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getTrackPlaybackStatus } from '@disco/state-management/selectors/media'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { attachGallery, getGalleryImages } from '@disco/utils/gallery'
import { HoverContext } from 'components/ui/hover-provider'
import Icon, { svgComponentMapping } from 'components/ui/icon'
import styles from './index.module.css'

const DEFAULT_TRACK_STATUS = {}

const getIconType = (
  fileType: FileType,
  playbackStatus: TrackStatus['playbackStatus'],
): keyof typeof svgComponentMapping => {
  if ([AUDIO_FILE, VIDEO_FILE].includes(fileType)) {
    return playbackStatus === PLAYING ? 'pause' : 'play'
  } else {
    return 'watch'
  }
}

type Props = {
  children: React.ReactNode
  disabled?: boolean
  location?: TrackLocation
  mediaSource: EventSource
  playbackContext: TrackOptions
  track: Track
  iconSize?: number
  clickEvent?: () => void
}

/**
 * Media overlay component that handles playback controls and file preview
 * Displays play/pause/watch icons on hover and manages media interactions
 * Supports audio, video, and non-media file types with appropriate handling
 * @param children - Content to overlay (typically artwork or media preview)
 * @param disabled - Whether interaction is disabled
 * @param location - Track location context for analytics
 * @param mediaSource - Source of the media for tracking
 * @param playbackContext - Playback context information
 * @param track - Track data for playback and preview
 */
const MediaOverlay = ({
  children,
  disabled,
  location,
  mediaSource,
  playbackContext,
  track,
  iconSize = 16,
  clickEvent,
}: Props) => {
  const dispatch = useDispatch()
  const { loadStatus, playbackStatus, playbackLocation }: Partial<TrackStatus> =
    useSelector(state =>
      getTrackPlaybackStatus(state, {
        trackId: track.id,
      }),
    ) ?? DEFAULT_TRACK_STATUS
  const fileType: FileType = getFileTypeByExt(track.ext)
  const isMediaFile: boolean = [AUDIO_FILE, VIDEO_FILE].includes(fileType)
  const handleFileClick = useCallback(
    (event: React.SyntheticEvent<any>): void => {
      event.preventDefault()
      event.stopPropagation()
      const { id, name, previews } = track
      const galleryImages = getGalleryImages(name, previews)
      attachGallery(`gallery-${id}`, galleryImages)
      const eventName = ['public-playlist', 'public-track'].includes(
        location ?? '',
      )
        ? 'Preview non-media file on public page'
        : 'Preview non-media file in app'
      if (clickEvent) {
        clickEvent()
      } else {
        mixpanel.sendPureEvent(eventName)
      }
      if (location === 'public-playlist') {
        GoogleAnalytics.sendEvent(
          'Other',
          'Preview non-media file on playlist page',
        )
      }

      if (location === 'public-track') {
        GoogleAnalytics.sendEvent(
          'Other',
          'Preview non-media file on track page',
        )
      }
    },
    [location, track, clickEvent],
  )
  const handleMediaClick = useCallback(
    (event: React.SyntheticEvent<any>): void => {
      event.preventDefault()
      event.stopPropagation()

      if (
        fileType === DRM_FILE ||
        loadStatus === LOADING ||
        track.outside_your_territories
      ) {
        return
      }
      if (playbackStatus === PLAYING) {
        dispatch(pauseTrack(track.id))
      } else {
        dispatch(
          playTrack(track.id, mediaSource, {
            ...playbackContext,
            playbackLocation:
              playbackContext?.playbackLocation || playbackLocation,
          }),
        )

        if (clickEvent) clickEvent()
      }
    },
    [
      dispatch,
      fileType,
      loadStatus,
      mediaSource,
      playbackContext,
      playbackStatus,
      playbackLocation,
      track,
      clickEvent,
    ],
  )
  const isPhone = useSelector(isDeviceTypePhone)
  const { isHover } = useContext(HoverContext)
  const overlayIconSize = isMediaFile ? iconSize : 24
  return disabled ? (
    children
  ) : (
    <div
      className={styles.mediaOverlay}
      onClick={isMediaFile ? handleMediaClick : handleFileClick}
    >
      <div
        className={cn(styles.mediaOverlayButton, {
          [styles.mediaOverlayButtonVisible]:
            (!isPhone && isHover) || playbackStatus === PLAYING,
        })}
      >
        <div className={styles.mediaOverlayIcon}>
          <Icon
            type={getIconType(fileType, playbackStatus)}
            width={overlayIconSize}
            height={overlayIconSize}
          />
        </div>
      </div>
      {children}
      {!isMediaFile && <div id={`gallery-${track.id}`} />}
    </div>
  )
}

export default React.memo<Props>(MediaOverlay)
